/**
 * Beta Status Banner
 * Displays beta user status with client count and days remaining
 * Shows warnings when limits are approaching
 */

import { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getBetaStatus } from '@/lib/betaHelpers';
import { useNavigate } from 'react-router-dom';

interface BetaStatus {
  isBetaUser: boolean;
  betaExpiresAt: string;
  betaActivatedAt: string;
  daysRemaining: number;
  isExpired: boolean;
  clientCount: number;
  clientLimit: number;
}

export function BetaStatusBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [betaStatus, setBetaStatus] = useState<BetaStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadBetaStatus() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const status = await getBetaStatus(user.id);
        setBetaStatus(status);
      } catch (error) {
        console.error('Error loading beta status:', error);
        setBetaStatus(null);
      } finally {
        setLoading(false);
      }
    }

    loadBetaStatus();
  }, [user?.id]);

  // Don't show banner if not a beta user or still loading
  if (loading || !betaStatus) {
    return null;
  }

  const { daysRemaining, isExpired, clientCount, clientLimit } = betaStatus;
  const clientsRemaining = clientLimit - clientCount;

  // Determine warning state
  const isLowOnDays = daysRemaining <= 2 && !isExpired;
  const isLowOnClients = clientsRemaining <= 2;
  const isWarning = isLowOnDays || isLowOnClients || isExpired;

  // Variant based on state
  const variant = isExpired ? 'destructive' : isWarning ? 'default' : 'default';

  const handleUpgrade = () => {
    navigate('/settings?tab=billing');
  };

  return (
    <Alert
      variant={variant}
      className={`mx-4 mt-4 border-l-4 ${
        isExpired
          ? 'border-l-destructive bg-destructive/10'
          : isWarning
          ? 'border-l-orange-500 bg-orange-500/10'
          : 'border-l-primary bg-primary/10'
      }`}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4">
          <Badge variant={isExpired ? 'destructive' : 'secondary'} className="text-sm font-medium">
            BETA
          </Badge>

          <AlertDescription className="flex items-center gap-4 text-sm">
            {/* Client count */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className={clientsRemaining <= 2 && !isExpired ? 'font-semibold text-orange-600' : ''}>
                {clientCount}/{clientLimit} clients
              </span>
            </div>

            {/* Days remaining */}
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className={isLowOnDays ? 'font-semibold text-orange-600' : ''}>
                {isExpired
                  ? 'Expired'
                  : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`
                }
              </span>
            </div>

            {/* Warning messages */}
            {isWarning && !isExpired && (
              <div className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">
                  {isLowOnDays && isLowOnClients
                    ? 'Running out of time and clients!'
                    : isLowOnDays
                    ? 'Beta ending soon!'
                    : 'Client limit almost reached!'}
                </span>
              </div>
            )}

            {isExpired && (
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-semibold">
                  Beta access expired. Upgrade to continue.
                </span>
              </div>
            )}
          </AlertDescription>
        </div>

        {/* Upgrade button */}
        {(isWarning || isExpired) && (
          <Button
            variant={isExpired ? 'destructive' : 'default'}
            size="sm"
            onClick={handleUpgrade}
          >
            {isExpired ? 'Upgrade Now' : 'Upgrade'}
          </Button>
        )}
      </div>
    </Alert>
  );
}
